/**
 * Preklad obsahu.
 *
 * Zdroj pravdy je import z unmatched.cards — anglický a generovaný. Preklady
 * ležia vedľa neho ako samostatná vrstva (`i18n/<locale>.json`), takže sa dajú
 * dopĺňať po balíčkoch bez zásahu do dát a bez čakania na kompletnosť:
 * **čo nie je preložené, ukáže sa po anglicky.**
 *
 * Prekladá sa text, nie pravidlá — engine číta `value`, `boost`, `type`
 * a `character`, a tie sú na jazyku nezávislé.
 */

import type { CardDef, HeroDef } from '@unmatched/engine';

import sk from '../i18n/sk.json' with { type: 'json' };

export type Locale = 'en' | 'sk';

export const LOCALES: Locale[] = ['en', 'sk'];

export interface CardEffects {
  basic: string | null;
  immediate: string | null;
  during: string | null;
  after: string | null;
}

/** Tvar záznamu v `i18n/<locale>.json`. Všetko je voliteľné. */
export interface ContentStrings {
  name?: string;
  special?: string;
  /** Fázy sa prekladajú zvlášť — šablóna ich kreslí ako samostatné riadky. */
  effects?: Partial<Record<keyof CardEffects, string | null>>;
}

const TRANSLATIONS: Record<Locale, Record<string, ContentStrings>> = {
  en: {},
  sk: sk as Record<string, ContentStrings>,
};

export interface LocalizedCard {
  name: string;
  effects: CardEffects;
  /** Ukazuje sa aspoň časť textu v cieľovom jazyku? */
  translated: boolean;
}

function pick(translated: string | null | undefined, original: string | null | undefined): string | null {
  const value = translated ?? original;
  return value && value.trim() ? value : null;
}

export function localizeCard(def: CardDef, locale: Locale = 'en'): LocalizedCard {
  const strings = TRANSLATIONS[locale]?.[def.id];
  const source = def.effects ?? {};
  return {
    name: strings?.name ?? def.name,
    effects: {
      basic: pick(strings?.effects?.basic, source.basic),
      immediate: pick(strings?.effects?.immediate, source.immediate),
      during: pick(strings?.effects?.during, source.during),
      after: pick(strings?.effects?.after, source.after),
    },
    translated: Boolean(strings),
  };
}

export interface LocalizedHero {
  name: string;
  special: string | null;
  translated: boolean;
}

export function localizeHero(hero: HeroDef, locale: Locale = 'en'): LocalizedHero {
  const strings = TRANSLATIONS[locale]?.[hero.id];
  return {
    name: strings?.name ?? hero.name,
    special: pick(strings?.special, hero.special),
    translated: Boolean(strings),
  };
}

/** Koľko záznamov daného jazyka je vyplnených — podklad pre `npm run i18n:check`. */
export function localeCoverage(locale: Locale): number {
  return Object.keys(TRANSLATIONS[locale] ?? {}).length;
}
